import React, { useEffect, useState, useCallback } from 'react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Building2, ChevronDown, Check } from 'lucide-react';

const STORAGE_KEY = 'portal_active_customer_id';

/**
 * Returns the currently-active customer_id, auto-syncing with the list
 * of available customers. If none is set, picks the first one.
 *
 * Usage:
 *   const { activeId, setActiveId } = useActiveCustomer(customers);
 *   // pass activeId to all `?customer_id=` fetches on the page
 *
 * The value is persisted in localStorage so it survives navigation.
 */
export function useActiveCustomer(customers = []) {
  const [activeId, setActiveIdState] = useState(() => localStorage.getItem(STORAGE_KEY) || null);

  useEffect(() => {
    if (!customers || customers.length === 0) return;
    const ids = customers.map(c => c.customer_id);
    if (!activeId || !ids.includes(activeId)) {
      const first = ids[0];
      setActiveIdState(first);
      localStorage.setItem(STORAGE_KEY, first);
    }
  }, [customers, activeId]);

  // React to switcher events from other components on the same tab.
  useEffect(() => {
    const onChange = (e) => {
      const v = e.detail?.customer_id || localStorage.getItem(STORAGE_KEY);
      setActiveIdState(v);
    };
    window.addEventListener('portal-active-customer-change', onChange);
    return () => window.removeEventListener('portal-active-customer-change', onChange);
  }, []);

  const setActiveId = useCallback((id) => {
    if (!id) return;
    localStorage.setItem(STORAGE_KEY, id);
    setActiveIdState(id);
    window.dispatchEvent(new CustomEvent('portal-active-customer-change', { detail: { customer_id: id } }));
  }, []);

  return { activeId, setActiveId };
}

/**
 * Dropdown selector shown in the portal page header when a user has
 * multiple linked customer accounts (by phone or email). Reloads the
 * page on switch so every child component re-fetches under the new id.
 */
export default function CustomerSwitcher({ customers = [], activeId, onChange }) {
  if (!customers || customers.length <= 1) return null;
  const active = customers.find(c => c.customer_id === activeId) || customers[0];

  const handleSelect = (id) => {
    if (id === activeId) return;
    onChange?.(id);
    // Full reload to re-fetch all scoped data with new customer_id
    setTimeout(() => window.location.reload(), 50);
  };

  const label = active?.company_name || active?.contact_name || 'Account';
  const short = label.length > 18 ? `${label.slice(0, 18)}…` : label;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 h-8" data-testid="customer-switcher-trigger">
          <Building2 className="h-3.5 w-3.5" />
          <span className="text-xs font-medium">{short}</span>
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" data-testid="customer-switcher-menu">
        <DropdownMenuLabel className="text-xs font-semibold text-slate-500">
          Switch Account ({customers.length})
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {customers.map(c => {
          const isActive = c.customer_id === activeId;
          return (
            <DropdownMenuItem
              key={c.customer_id}
              onClick={() => handleSelect(c.customer_id)}
              className="flex items-start gap-2 py-2"
              data-testid={`customer-switcher-opt-${c.customer_id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{c.company_name || c.contact_name || c.customer_id}</p>
                <p className="text-xs text-slate-500 truncate">
                  {c.customer_type === 'partner' ? 'Partner' : c.parent_partner_id ? 'LCO' : 'Customer'}
                  {c.customer_code ? ` • ${c.customer_code}` : ''}
                </p>
              </div>
              {isActive && <Check className="h-4 w-4 text-emerald-600 mt-1" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
